import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useSearch } from "wouter";
import { Link } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { CheckCircle, ArrowLeft } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import type { MembershipTier } from "@shared/schema";

const applySchema = z.object({
  companyName: z.string().min(2, "Company name is required"),
  contactName: z.string().min(2, "Contact name is required"),
  contactEmail: z.string().email("Valid email required"),
  contactPhone: z.string().optional(),
  website: z.string().url("Enter a valid URL (include https://)").optional().or(z.literal("")),
  tierId: z.string().min(1, "Please select a membership tier"),
  message: z.string().optional(),
});

type ApplyForm = z.infer<typeof applySchema>;

export default function MembershipApplyPage() {
  const search = useSearch();
  const params = new URLSearchParams(search);
  const preselectedTierSlug = params.get("tier");
  const [submitted, setSubmitted] = useState(false);

  const { data: tiers = [] } = useQuery<MembershipTier[]>({
    queryKey: ["/api/public/membership-tiers"],
  });

  const preselectedTier = tiers.find(t => t.slug === preselectedTierSlug);

  const form = useForm<ApplyForm>({
    resolver: zodResolver(applySchema),
    defaultValues: {
      companyName: "",
      contactName: "",
      contactEmail: "",
      contactPhone: "",
      website: "",
      tierId: preselectedTier?.id ?? "",
      message: "",
    },
  });

  // Update tierId when tiers load and we have a preselected slug
  const tierIdValue = form.watch("tierId");
  if (preselectedTier && !tierIdValue && tiers.length > 0) {
    form.setValue("tierId", preselectedTier.id);
  }

  const mutation = useMutation({
    mutationFn: (data: ApplyForm) =>
      apiRequest("POST", "/api/public/membership-applications", data),
    onSuccess: () => {
      setSubmitted(true);
    },
  });

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center p-8">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Application Received!</h2>
          <p className="text-muted-foreground mb-6">
            Thank you for your interest in Future Manufacturing membership. Our team will review your application and be in touch within 2–3 business days.
          </p>
          <Button asChild variant="outline">
            <Link href="/membership"><ArrowLeft className="h-4 w-4 mr-2" />Back to Membership</Link>
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-xl mx-auto">
        <div className="mb-6">
          <Link href="/membership" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
            <ArrowLeft className="h-3.5 w-3.5" />Back to Membership
          </Link>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Apply for Membership</CardTitle>
            <p className="text-muted-foreground text-sm">
              Fill in your details below and a member of our team will be in touch to complete your membership.
            </p>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(d => mutation.mutate(d))} className="space-y-4">
                <FormField
                  control={form.control}
                  name="tierId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Membership Tier</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger data-testid="select-apply-tier">
                            <SelectValue placeholder="Select a tier…" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {tiers.map(t => (
                            <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="companyName"
                    render={({ field }) => (
                      <FormItem className="col-span-2">
                        <FormLabel>Company Name</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Acme Manufacturing Ltd" data-testid="input-company-name" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="website"
                    render={({ field }) => (
                      <FormItem className="col-span-2">
                        <FormLabel>Company Website</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="https://example.com" data-testid="input-website" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="contactName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Your Name</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Jane Smith" data-testid="input-contact-name" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="contactEmail"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email Address</FormLabel>
                        <FormControl>
                          <Input {...field} type="email" placeholder="jane@example.com" data-testid="input-contact-email" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="contactPhone"
                    render={({ field }) => (
                      <FormItem className="col-span-2">
                        <FormLabel>Phone Number (optional)</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="+44 20 1234 5678" data-testid="input-contact-phone" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="message"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Anything else you'd like us to know? (optional)</FormLabel>
                      <FormControl>
                        <Textarea {...field} rows={3} placeholder="Tell us about your company, content goals, or questions…" data-testid="input-message" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {mutation.isError && (
                  <p className="text-sm text-destructive">
                    Something went wrong. Please try again.
                  </p>
                )}

                <Button type="submit" className="w-full" disabled={mutation.isPending} data-testid="button-submit-application">
                  {mutation.isPending ? "Submitting…" : "Submit Application"}
                </Button>

                <p className="text-xs text-muted-foreground text-center">
                  By submitting this form you agree to our{" "}
                  <Link href="/page/terms" className="underline hover:text-foreground">Terms</Link>{" "}
                  and{" "}
                  <Link href="/page/privacy" className="underline hover:text-foreground">Privacy Policy</Link>.
                </p>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
